import type { CSSProperties } from 'react';
import { API_BASE_URL } from '../../config/api';

export interface RasffNotification {
  rasff_id: number;
  notif_id: number;
  reference: string;
  subject: string;
  ec_validation_date: string | null;
  notifying_country: string | null;
  product_category: string | null;
  product_type: string | null;
  classification: string | null;
  risk_decision: string | null;
  hazard_names: string | null;
  origin_countries: string | null;
  distribution_countries: string | null;
  dim_hazards: {
    dimhazards_id: number;
    hazardname: string;
    hazardscategoryname: string;
  } | null;
}

export type FilterKey =
  | 'category' | 'hazardCategory' | 'hazard' | 'classification'
  | 'riskDecision' | 'originCountry' | 'notifyingCountry' | 'distCountry' | 'productType';

export interface Filters {
  category: string | null;
  hazardCategory: string | null;
  hazard: string | null;
  classification: string | null;
  riskDecision: string | null;
  originCountry: string | null;
  notifyingCountry: string | null;
  distCountry: string | null;
  productType: string | null;
}

export const EMPTY_FILTERS: Filters = {
  category: null, hazardCategory: null, hazard: null, classification: null,
  riskDecision: null, originCountry: null, notifyingCountry: null,
  distCountry: null, productType: null,
};

export const TIME_FRAMES = [
  { label: '7d',   days: 7 },
  { label: '14d',  days: 14 },
  { label: '30d',  days: 30 },
  { label: '90d',  days: 90 },
  { label: '180d', days: 180 },
  { label: '1y',   days: 365 },
  { label: 'All',  days: 0 },
];

/** FSNU Sentinel Watch brand colors mapped to RASFF classification semantics */
export const CLASS_COLORS: Record<string, string> = {
  'Alert':            '#C44536',  // Coral 600
  'Border Rejection': '#C77B1A',  // Amber 500
  'Info (Attention)': '#3D5A80',  // Slate Blue 500
  'Info (Follow-up)': '#52B788',  // Forest 400
  'Non-compliance':   '#6B6354',  // Warm 600
  'Other':            '#8D8272',  // Warm 500
};

export const CHART_COLORS = [
  '#1B4332', '#3D5A80', '#C77B1A', '#C44536', '#52B788',
  '#2C4A6E', '#89A4C4', '#D4922B', '#40916C', '#6B6354',
  '#74C69D', '#A64B3A',
];

export const YEAR_COLORS = ['#3D5A80', '#52B788', '#C77B1A', '#C44536', '#89A4C4', '#74C69D', '#2C4A6E'];

/** Stable category → color. Hashing the *name* (djb2) instead of indexing by
 *  rank means a category keeps its color when filters re-order the data. */
export function getCategoryColor(name: string): string {
  let hash = 5381;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) + hash + name.charCodeAt(i)) | 0;
  }
  return CHART_COLORS[Math.abs(hash) % CHART_COLORS.length];
}

export const TOOLTIP_STYLE = {
  background: 'var(--color-bg-subtle)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  color: 'var(--color-text)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
  fontSize: '12px',
};

/** Shared inline style for custom tooltip content divs — matches TOOLTIP_STYLE */
export const TOOLTIP_CONTENT_STYLE: CSSProperties = {
  background: 'var(--color-bg-subtle)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  padding: '8px 12px',
  fontSize: 12,
  color: 'var(--color-text)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
  lineHeight: 1.5,
};

export async function fetchRasff(days: number): Promise<RasffNotification[]> {
  const res = await fetch(`${API_BASE_URL}/api/rasff-notifications?days=${days}`);
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  return json.notifications;
}

export function getVal(n: RasffNotification, key: FilterKey): string {
  switch (key) {
    case 'category':         return n.product_category || 'Unknown';
    case 'hazardCategory':   return n.dim_hazards?.hazardscategoryname || 'Unknown';
    case 'hazard':           return n.dim_hazards?.hazardname || 'Unknown';
    case 'classification':   return n.classification || 'Unknown';
    case 'riskDecision':     return n.risk_decision || 'Unknown';
    case 'productType':      return n.product_type || 'Unknown';
    case 'notifyingCountry': return n.notifying_country || 'Unknown';
    case 'originCountry':    return n.origin_countries || '';
    case 'distCountry':      return n.distribution_countries || '';
  }
}

export function matchesFilter(n: RasffNotification, key: FilterKey, value: string): boolean {
  if (key === 'originCountry') {
    return n.origin_countries ? n.origin_countries.split(', ').includes(value) : false;
  }
  if (key === 'distCountry') {
    return n.distribution_countries ? n.distribution_countries.split(', ').includes(value) : false;
  }
  return getVal(n, key) === value;
}

export function applyFilters(
  data: RasffNotification[],
  filters: Filters,
  exclude?: FilterKey,
): RasffNotification[] {
  return data.filter(n => {
    for (const [k, v] of Object.entries(filters) as [FilterKey, string | null][]) {
      if (!v || k === exclude) continue;
      if (!matchesFilter(n, k, v)) return false;
    }
    return true;
  });
}

export function countField(
  data: RasffNotification[],
  key: FilterKey,
): { name: string; value: number }[] {
  const counts: Record<string, number> = {};
  for (const n of data) {
    if (key === 'originCountry' || key === 'distCountry') {
      const raw = key === 'originCountry' ? n.origin_countries : n.distribution_countries;
      if (raw) {
        for (const c of raw.split(', ')) counts[c] = (counts[c] || 0) + 1;
      } else {
        counts['Unknown'] = (counts['Unknown'] || 0) + 1;
      }
    } else {
      const v = getVal(n, key);
      counts[v] = (counts[v] || 0) + 1;
    }
  }
  return Object.entries(counts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

/** Normalise a raw RASFF classification string to one of 6 display buckets */
export function classifyType(classification: string | null): string {
  const c = (classification || '').toLowerCase();
  if (c.includes('alert'))                                          return 'Alert';
  if (c.includes('border'))                                         return 'Border Rejection';
  if (c.includes('attention'))                                      return 'Info (Attention)';
  if (c.includes('follow'))                                         return 'Info (Follow-up)';
  if (c.includes('non-compliance') || c.includes('noncompliance')) return 'Non-compliance';
  return 'Other';
}

/** Stacked-bar data for top-N origin countries, segmented by classification (Springer Fig 6) */
export function buildOriginCountryData(data: RasffNotification[], topN = 10) {
  const counts: Record<string, Record<string, number>> = {};
  for (const n of data) {
    const origins = n.origin_countries
      ? n.origin_countries.split(', ').filter(Boolean)
      : ['Unknown'];
    const cls = classifyType(n.classification);
    for (const country of origins) {
      if (!counts[country]) counts[country] = {};
      counts[country][cls] = (counts[country][cls] || 0) + 1;
    }
  }
  return Object.entries(counts)
    .map(([country, cls]) => ({
      country,
      'Alert':            cls['Alert']            || 0,
      'Border Rejection': cls['Border Rejection'] || 0,
      'Info (Attention)': cls['Info (Attention)'] || 0,
      'Info (Follow-up)': cls['Info (Follow-up)'] || 0,
      'Non-compliance':   cls['Non-compliance']   || 0,
      total: Object.values(cls).reduce((a, b) => a + b, 0),
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, topN);
}

/** Bubble chart data for product × hazard co-occurrence (Springer Fig 14) */
export function buildProductHazardBubbles(
  data: RasffNotification[],
  topProducts = 8,
  topHazards = 6,
  useHazardName = false,
) {
  const counts: Record<string, Record<string, number>> = {};
  for (const n of data) {
    const product = n.product_category || 'Unknown';
    const hazard  = useHazardName
      ? (n.dim_hazards?.hazardname || 'Unknown')
      : (n.dim_hazards?.hazardscategoryname || 'Unknown');
    if (!counts[product]) counts[product] = {};
    counts[product][hazard] = (counts[product][hazard] || 0) + 1;
  }

  const productNames = Object.entries(counts)
    .map(([name, h]) => ({ name, total: Object.values(h).reduce((a, b) => a + b, 0) }))
    .sort((a, b) => b.total - a.total)
    .slice(0, topProducts)
    .map(p => p.name);

  const hazardTotals: Record<string, number> = {};
  for (const product of productNames) {
    for (const [hazard, cnt] of Object.entries(counts[product] || {})) {
      hazardTotals[hazard] = (hazardTotals[hazard] || 0) + cnt;
    }
  }
  const hazardNames = Object.entries(hazardTotals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topHazards)
    .map(h => h[0]);

  const bubbles: { x: number; y: number; z: number; productName: string; hazardName: string }[] = [];
  for (const [pi, product] of productNames.entries()) {
    for (const [hi, hazard] of hazardNames.entries()) {
      const z = counts[product]?.[hazard] || 0;
      if (z > 0) bubbles.push({ x: pi, y: hi, z, productName: product, hazardName: hazard });
    }
  }
  return { bubbles, productNames, hazardNames };
}

/** Timeline data grouped by month or week for ComposedChart (Aydin Fig 1 / Springer S1–S3) */
export function buildClassificationTimeline(
  data: RasffNotification[],
  bucket: 'month' | 'week',
) {
  type Row = {
    label: string; sortKey: string;
    Alert: number; 'Border Rejection': number;
    'Info (Attention)': number; 'Info (Follow-up)': number;
    'Non-compliance': number; Other: number;
  };
  const buckets: Record<string, Row> = {};

  for (const n of data) {
    if (!n.ec_validation_date) continue;
    const date = new Date(n.ec_validation_date);
    let sortKey: string;
    let label: string;

    if (bucket === 'month') {
      sortKey = n.ec_validation_date.substring(0, 7);
      label   = date.toLocaleString('en', { month: 'short', year: '2-digit' });
    } else {
      const year  = date.getFullYear();
      const start = new Date(year, 0, 1);
      const week  = Math.ceil(
        ((date.getTime() - start.getTime()) / 86400000 + start.getDay() + 1) / 7,
      );
      sortKey = `${year}-W${String(week).padStart(2, '0')}`;
      label   = `W${week} '${String(year).slice(2)}`;
    }

    if (!buckets[sortKey]) {
      buckets[sortKey] = {
        label, sortKey,
        Alert: 0, 'Border Rejection': 0,
        'Info (Attention)': 0, 'Info (Follow-up)': 0,
        'Non-compliance': 0, Other: 0,
      };
    }
    const cls = classifyType(n.classification) as keyof Row;
    (buckets[sortKey][cls] as number)++;
  }

  return Object.values(buckets).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
}

/** Origin × notifying country bubble data (Springer Fig 10) */
export function buildCountryPairBubbles(data: RasffNotification[], topN = 10) {
  const pairs: Record<string, Record<string, number>>  = {};
  const originTotals: Record<string, number>           = {};
  const notifyingTotals: Record<string, number>        = {};

  for (const n of data) {
    const origins   = n.origin_countries
      ? n.origin_countries.split(', ').filter(Boolean)
      : ['Unknown'];
    const notifying = n.notifying_country || 'Unknown';
    notifyingTotals[notifying] = (notifyingTotals[notifying] || 0) + 1;
    for (const origin of origins) {
      originTotals[origin] = (originTotals[origin] || 0) + 1;
      if (!pairs[origin]) pairs[origin] = {};
      pairs[origin][notifying] = (pairs[origin][notifying] || 0) + 1;
    }
  }

  const topOrigins   = Object.entries(originTotals)
    .sort((a, b) => b[1] - a[1]).slice(0, topN).map(e => e[0]);
  const topNotifying = Object.entries(notifyingTotals)
    .sort((a, b) => b[1] - a[1]).slice(0, topN).map(e => e[0]);

  const bubbles: { x: number; y: number; z: number; originName: string; notifyingName: string }[] = [];
  for (const [oi, origin] of topOrigins.entries()) {
    for (const [ni, notifying] of topNotifying.entries()) {
      const z = pairs[origin]?.[notifying] || 0;
      if (z > 0) bubbles.push({ x: oi, y: ni, z, originName: origin, notifyingName: notifying });
    }
  }

  return { bubbles, topOrigins, topNotifying };
}
